// frontend\src\components\Dashboard.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import LoadingSpinner from './LoadingSpinner';
import MyTasksCard from './tasks/MyTasksCard';
import { useAuth } from '../context/AuthContext';
import { useTasks } from '../context/TasksContext';
import { useTableSort, SortableTh, SortComparators } from '../hooks/useTableSort';
import '../dashboard.css';

interface LowStockItem {
  product_name: string;
  sku: string;
  total_stock: number;
  avg_sales_per_month: number;
  days_left: number | null;
  target_stock_days: number;
  estimated_restock_date: string | null;
}

/**
 * Days Left sorting for the low-stock table. Out-of-stock items (no sellable
 * stock) all have zero-or-negative cover, so ordering them by days left is
 * meaningless — instead they group together at the top and rank by sales
 * velocity, putting the items we sell the most of first. Everything else sorts
 * by days left as usual. Module-level so the sort memo stays stable.
 */
const lowStockComparators: SortComparators<LowStockItem> = {
  days_left: (a, b) => {
    const aOut = a.total_stock <= 0;
    const bOut = b.total_stock <= 0;
    if (aOut !== bOut) return aOut ? -1 : 1;
    if (aOut && bOut) return b.avg_sales_per_month - a.avg_sales_per_month;
    return (a.days_left ?? 0) - (b.days_left ?? 0);
  },
};

interface WarehouseGap {
  warehouse: string;
  product_name: string;
  sku: string;
  stock: number;
  shipped_90d: number;
  days_left: number;
}

interface OnHoldOrder {
  id: number;
  customer_name: string;
  date_created: string;
  total: number;
  age_days: number;
}

interface OpenOrder extends OnHoldOrder {
  status: string;
  items_total: number;
  items_shipped: number;
}

interface DashboardPO {
  id: number;
  po_number: string;
  supplier: string;
  status: string;
  estimated_date: string | null;
  overdue: boolean;
}

interface PendingShipment {
  order_number: number;
  warehouse: string;
  date_shipped: string;
  shipment_reference: string;
  pending_items: number;
  age_hours: number;
}

/** Subset of the Inputs payload the dashboard needs. */
interface DashboardInput {
  id: number;
  name: string;
  supplier_name: string | null;
  current_stock: number;
  burn_rate: number;
  days_cover: number | null;
  delivery_days: number;
  safety_days: number;
  reorder_by: string | null;
  health: string;
  /** days_cover minus lead time — how long until an order must be placed. */
  days_until_order: number;
}

interface DashboardData {
  kpis: {
    revenue7d: number;
    orders7d: number;
    lowStockCount: number;
    outOfStockCount: number;
    onHoldCount: number;
    openOrdersCount: number;
    openPOsCount: number;
    overduePOsCount: number;
    pendingShipmentsCount: number;
  };
  lowStock: LowStockItem[];
  warehouseGaps: WarehouseGap[];
  onHoldOrders: OnHoldOrder[];
  openOrders: OpenOrder[];
  purchaseOrders: DashboardPO[];
  pendingShipments: PendingShipment[];
  generated_at: string;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const formatAge = (hours: number) => {
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

const daysChip = (daysLeft: number | null) => {
  if (daysLeft === null) return null;
  const rounded = Math.floor(daysLeft);
  const cls = rounded < 7 ? 'danger' : rounded < 14 ? 'warn' : 'ok';
  return (
    <span className={`dash-chip ${cls}`}>
      {rounded <= 0 ? 'Out of stock' : `${rounded} day${rounded === 1 ? '' : 's'}`}
    </span>
  );
};

/** Inputs needing an order placed within this many days are surfaced here. */
const INPUT_REORDER_HORIZON_DAYS = 14;

/** Days until an order must be placed. Zero or less means it is already late. */
const orderWithinChip = (days: number) => {
  const rounded = Math.floor(days);
  if (rounded <= 0) return <span className="dash-chip danger">Order now</span>;
  const cls = rounded < 7 ? 'warn' : 'ok';
  return <span className={`dash-chip ${cls}`}>{rounded} day{rounded === 1 ? '' : 's'}</span>;
};

const AdminDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [inputs, setInputs] = useState<DashboardInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/dashboard');
      setData(response.data);
    } catch (err: any) {
      console.error('Dashboard fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetched separately from /dashboard: /api/inputs enforces Admin server-side,
  // whereas /api/dashboard does not, so inputs data never rides along on a
  // response a non-admin could request. A failure here must not break the page.
  const fetchInputs = useCallback(async () => {
    try {
      const response = await api.get('/inputs');
      const rows: DashboardInput[] = (response.data.inputs || [])
        .map((input: any) => ({
          ...input,
          days_until_order:
            input.days_cover === null
              ? Number.POSITIVE_INFINITY
              : input.days_cover - (input.delivery_days + input.safety_days),
        }))
        .filter((input: DashboardInput) => input.days_until_order <= INPUT_REORDER_HORIZON_DAYS);
      setInputs(rows);
    } catch (err) {
      console.error('Dashboard inputs fetch error:', err);
      setInputs([]);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchInputs();
  }, [fetchData, fetchInputs]);

  const lowStockSort = useTableSort<LowStockItem>(data?.lowStock || [], 'days_left', 'asc', lowStockComparators);
  const gapsSort = useTableSort<WarehouseGap>(data?.warehouseGaps || [], 'days_left', 'asc');
  const onHoldSort = useTableSort<OnHoldOrder>(data?.onHoldOrders || [], 'age_days', 'desc');
  const openOrdersSort = useTableSort<OpenOrder>(data?.openOrders || [], 'age_days', 'desc');
  const poSort = useTableSort<DashboardPO>(data?.purchaseOrders || [], 'estimated_date', 'asc');
  const shipmentsSort = useTableSort<PendingShipment>(data?.pendingShipments || [], 'age_hours', 'desc');
  const inputsSort = useTableSort<DashboardInput>(inputs, 'days_until_order', 'asc');

  if (loading) return <LoadingSpinner />;
  if (error) {
    return (
      <div className="dash-container">
        <div className="dash-error">
          Error: {error}{' '}
          <button className="dash-retry-button" onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { kpis } = data;

  const kpiCards: { label: string; value: string; sub: string; alert?: boolean; to?: string }[] = [
    { label: '7-Day Revenue', value: formatCurrency(kpis.revenue7d), sub: `${kpis.orders7d} orders`, to: '/analytics' },
    {
      label: 'Low Stock Items',
      value: String(kpis.lowStockCount),
      sub: `${kpis.outOfStockCount} out of stock`,
      alert: kpis.outOfStockCount > 0,
    },
    {
      label: 'On-Hold Orders',
      value: String(kpis.onHoldCount),
      sub: 'need follow-up',
      alert: kpis.onHoldCount > 0,
      to: '/orders',
    },
    { label: 'Open Orders', value: String(kpis.openOrdersCount), sub: 'processing / partial', to: '/orders' },
    {
      label: 'Pending Shipments',
      value: String(kpis.pendingShipmentsCount),
      sub: 'label created, not shipped',
      alert: data.pendingShipments.some((s) => s.age_hours >= 36),
    },
    {
      label: 'Open POs',
      value: String(kpis.openPOsCount),
      sub: `${kpis.overduePOsCount} overdue`,
      alert: kpis.overduePOsCount > 0,
      to: '/purchase-orders',
    },
  ];

  return (
    <div className="dash-container">
      <div className="dash-header">
        <h1>Dashboard</h1>
        <span className="dash-updated">Updated {new Date(data.generated_at).toLocaleTimeString('en-US')}</span>
      </div>

      <div className="dash-kpi-grid">
        {kpiCards.map((kpi) => (
          <div
            className={`dash-kpi-card ${kpi.alert ? 'alert' : ''} ${kpi.to ? 'clickable' : ''}`}
            key={kpi.label}
            onClick={kpi.to ? () => navigate(kpi.to!) : undefined}
            role={kpi.to ? 'link' : undefined}
            title={kpi.to ? `Go to ${kpi.to.replace('/', '').replace('-', ' ')}` : undefined}
          >
            <span className="dash-kpi-label">{kpi.label}</span>
            <span className="dash-kpi-value">{kpi.value}</span>
            <span className="dash-kpi-sub">{kpi.sub}</span>
          </div>
        ))}
      </div>

      <MyTasksCard />

      {/* ── Low stock (critical) ─────────────────────────────────── */}
      <div className="dash-card dash-card-critical">
        <div className="dash-card-header">
          <h3>Low &amp; Out of Stock — Selling Items</h3>
          <Link to="/stock" className="dash-link">Go to Stock →</Link>
        </div>
        <p className="dash-card-note">
          Items that sell regularly, ranked by days of stock remaining (stock ÷ average daily sales).
          Click a row to update its website message or estimated restock date.
        </p>
        {data.lowStock.length === 0 ? (
          <p className="dash-empty">Nothing low — all selling items have healthy cover. 🎉</p>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <SortableTh label="Product" column="product_name" sort={lowStockSort} />
                  <SortableTh label="Stock" column="total_stock" center sort={lowStockSort} />
                  <SortableTh label="Avg Sales / Mo" column="avg_sales_per_month" center sort={lowStockSort} />
                  <SortableTh label="Days Left" column="days_left" center sort={lowStockSort} />
                  <SortableTh label="Target" column="target_stock_days" center sort={lowStockSort} />
                  <SortableTh label="Est. Restock" column="estimated_restock_date" sort={lowStockSort} />
                </tr>
              </thead>
              <tbody>
                {lowStockSort.sorted.map((item) => (
                  <tr
                    key={item.sku}
                    className="dash-row-clickable"
                    title="Open product details on the Stock page"
                    onClick={() => navigate(`/stock?product=${encodeURIComponent(item.sku)}`)}
                  >
                    <td>{item.product_name} <span className="dash-sku">({item.sku})</span></td>
                    <td style={{ textAlign: 'center' }}>{item.total_stock}</td>
                    <td style={{ textAlign: 'center' }}>{item.avg_sales_per_month.toFixed(1)}</td>
                    <td style={{ textAlign: 'center' }}>{daysChip(item.days_left)}</td>
                    <td style={{ textAlign: 'center' }}>{item.target_stock_days}d</td>
                    <td>{item.estimated_restock_date ? formatDate(item.estimated_restock_date) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Inputs needing reorder ───────────────────────────────── */}
      {inputs.length > 0 && (
        <div className="dash-card dash-card-critical">
          <div className="dash-card-header">
            <h3>Inputs To Reorder</h3>
            <Link to="/inputs" className="dash-link">Go to Inputs →</Link>
          </div>
          <p className="dash-card-note">
            Consumables that must be ordered within {INPUT_REORDER_HORIZON_DAYS} days to avoid
            running out, accounting for each supplier's delivery time.
          </p>
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <SortableTh label="Input" column="name" sort={inputsSort} />
                  <SortableTh label="Supplier" column="supplier_name" sort={inputsSort} />
                  <SortableTh label="Stock" column="current_stock" center sort={inputsSort} />
                  <SortableTh label="Burn / Mo" column="burn_rate" center sort={inputsSort} />
                  <SortableTh label="Days Cover" column="days_cover" center sort={inputsSort} />
                  <SortableTh label="Lead Time" column="delivery_days" center sort={inputsSort} />
                  <SortableTh label="Order Within" column="days_until_order" center sort={inputsSort} />
                  <SortableTh label="Order By" column="reorder_by" sort={inputsSort} />
                </tr>
              </thead>
              <tbody>
                {inputsSort.sorted.map((input) => (
                  <tr
                    key={input.id}
                    className="dash-row-clickable"
                    title="Open the Inputs page"
                    onClick={() => navigate('/inputs')}
                  >
                    <td>{input.name}</td>
                    <td>{input.supplier_name || '—'}</td>
                    <td style={{ textAlign: 'center' }}>{input.current_stock}</td>
                    <td style={{ textAlign: 'center' }}>{input.burn_rate}</td>
                    <td style={{ textAlign: 'center' }}>{daysChip(input.days_cover)}</td>
                    <td style={{ textAlign: 'center' }}>{input.delivery_days + input.safety_days}d</td>
                    <td style={{ textAlign: 'center' }}>
                      {orderWithinChip(input.days_until_order)}
                    </td>
                    <td>{input.reorder_by ? formatDate(input.reorder_by) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Warehouse gaps (full width) ──────────────────────────── */}
      <div className="dash-card">
        <div className="dash-card-header">
          <h3>Warehouse Stock Gaps</h3>
        </div>
        <p className="dash-card-note">
          Items each warehouse has shipped regularly in the last 90 days, where that warehouse is nearly empty.
        </p>
        {data.warehouseGaps.length === 0 ? (
          <p className="dash-empty">No gaps — every warehouse can cover what it usually ships.</p>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <SortableTh label="Warehouse" column="warehouse" sort={gapsSort} />
                  <SortableTh label="Product" column="product_name" sort={gapsSort} />
                  <SortableTh label="Stock" column="stock" center sort={gapsSort} />
                  <SortableTh label="Shipped 90d" column="shipped_90d" center sort={gapsSort} />
                  <SortableTh label="Days Left" column="days_left" center sort={gapsSort} />
                </tr>
              </thead>
              <tbody>
                {gapsSort.sorted.map((gap, i) => (
                  <tr key={`${gap.warehouse}-${gap.sku}-${i}`}>
                    <td className="dash-warehouse">{gap.warehouse}</td>
                    <td>{gap.product_name} <span className="dash-sku">({gap.sku})</span></td>
                    <td style={{ textAlign: 'center' }}>{gap.stock}</td>
                    <td style={{ textAlign: 'center' }}>{gap.shipped_90d}</td>
                    <td style={{ textAlign: 'center' }}>{daysChip(gap.days_left)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="dash-grid-3">
        {/* ── Pending shipments ──────────────────────────────────── */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3>Pending Label Shipments</h3>
          </div>
          <p className="dash-card-note">
            Gelinas / MPP labels created but not yet scanned as shipped.
          </p>
          {data.pendingShipments.length === 0 ? (
            <p className="dash-empty">All labels shipped — nothing waiting.</p>
          ) : (
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <SortableTh label="Order" column="order_number" sort={shipmentsSort} />
                    <SortableTh label="Warehouse" column="warehouse" sort={shipmentsSort} />
                    <SortableTh label="Items" column="pending_items" center sort={shipmentsSort} />
                    <SortableTh label="Age" column="age_hours" center sort={shipmentsSort} />
                  </tr>
                </thead>
                <tbody>
                  {shipmentsSort.sorted.map((s) => (
                    <tr key={`${s.order_number}-${s.shipment_reference}`}>
                      <td>#{s.order_number}</td>
                      <td className="dash-warehouse">{s.warehouse}</td>
                      <td style={{ textAlign: 'center' }}>{s.pending_items}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`dash-chip ${s.age_hours >= 36 ? 'danger' : 'ok'}`}>
                          {formatAge(s.age_hours)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── On-hold orders ─────────────────────────────────────── */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3>On-Hold Orders</h3>
            <Link to="/orders" className="dash-link">Go to Orders →</Link>
          </div>
          {data.onHoldOrders.length === 0 ? (
            <p className="dash-empty">No orders on hold.</p>
          ) : (
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <SortableTh label="Order" column="id" sort={onHoldSort} />
                    <SortableTh label="Customer" column="customer_name" sort={onHoldSort} />
                    <SortableTh label="Total" column="total" sort={onHoldSort} />
                    <SortableTh label="Age" column="age_days" center sort={onHoldSort} />
                  </tr>
                </thead>
                <tbody>
                  {onHoldSort.sorted.map((o) => (
                    <tr key={o.id}>
                      <td>#{o.id}</td>
                      <td>{o.customer_name}</td>
                      <td>{formatCurrency(o.total)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`dash-chip ${o.age_days >= 3 ? 'danger' : 'warn'}`}>{o.age_days}d</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Purchase orders ────────────────────────────────────── */}
        <div className="dash-card">
          <div className="dash-card-header">
            <h3>Purchase Orders In Flight</h3>
            <Link to="/purchase-orders" className="dash-link">Go to POs →</Link>
          </div>
          {data.purchaseOrders.length === 0 ? (
            <p className="dash-empty">No open purchase orders.</p>
          ) : (
            <div className="dash-table-wrap">
              <table className="dash-table">
                <thead>
                  <tr>
                    <SortableTh label="PO #" column="po_number" sort={poSort} />
                    <SortableTh label="Supplier" column="supplier" sort={poSort} />
                    <SortableTh label="Status" column="status" sort={poSort} />
                    <SortableTh label="Expected" column="estimated_date" sort={poSort} />
                  </tr>
                </thead>
                <tbody>
                  {poSort.sorted.map((po) => (
                    <tr key={po.id}>
                      <td>{po.po_number}</td>
                      <td>{po.supplier}</td>
                      <td><span className="dash-chip neutral">{po.status}</span></td>
                      <td>
                        {po.estimated_date ? (
                          <span className={po.overdue ? 'dash-overdue' : ''}>
                            {formatDate(po.estimated_date)}{po.overdue ? ' (overdue)' : ''}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Open orders ──────────────────────────────────────────── */}
      <div className="dash-card">
        <div className="dash-card-header">
          <h3>Open Orders (Processing &amp; Partially Shipped)</h3>
          <Link to="/orders" className="dash-link">Go to Orders →</Link>
        </div>
        {data.openOrders.length === 0 ? (
          <p className="dash-empty">No open orders — everything has shipped.</p>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <SortableTh label="Order" column="id" sort={openOrdersSort} />
                  <SortableTh label="Customer" column="customer_name" sort={openOrdersSort} />
                  <SortableTh label="Status" column="status" sort={openOrdersSort} />
                  <SortableTh label="Total" column="total" sort={openOrdersSort} />
                  <SortableTh label="Shipped" column="items_shipped" center sort={openOrdersSort} />
                  <SortableTh label="Age" column="age_days" center sort={openOrdersSort} />
                </tr>
              </thead>
              <tbody>
                {openOrdersSort.sorted.map((o) => (
                  <tr key={o.id}>
                    <td>#{o.id}</td>
                    <td>{o.customer_name}</td>
                    <td>
                      <span className={`dash-chip ${o.status === 'partial-shipped' ? 'warn' : 'neutral'}`}>
                        {o.status}
                      </span>
                    </td>
                    <td>{formatCurrency(o.total)}</td>
                    <td style={{ textAlign: 'center' }}>
                      {o.items_shipped} / {o.items_total}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`dash-chip ${o.age_days >= 5 ? 'danger' : o.age_days >= 2 ? 'warn' : 'ok'}`}>
                        {o.age_days}d
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Warehouse dashboard ─────────────────────────────────────────────────────

interface WarehouseOrderRow {
  shipment_id: number;
  warehouse_id: number;
  warehouse: string;
  order_id: number;
  status: string;
  customer_name: string;
  date_created: string;
  age_days: number;
  total_items: number;
  pending_items: number;
  pending_names: string;
  has_label: boolean;
  /** Shipment Notes from the warehouse orders page. */
  notes: { note: string; added_by: string; date_added: string }[];
}

interface WarehouseDashboardData {
  warehouses: { id: number; name: string }[];
  orders: WarehouseOrderRow[];
  kpis: { toShip: number; old: number; withNotes: number };
  old_order_days: number;
  generated_at: string;
}

interface LowInput {
  id: number;
  name: string;
  current_stock: number;
  days_cover: number | null;
  health: string;
}

const LISTS_SHOWN = 8;

const ageChip = (days: number, oldDays: number) => {
  const cls = days >= oldDays ? 'danger' : days >= 1 ? 'warn' : 'ok';
  return <span className={`dash-chip ${cls}`}>{days === 0 ? 'Today' : `${days}d`}</span>;
};

/**
 * Regular users with a warehouse (Admin › Users): what their warehouse has to
 * ship — oldest first, notes called out — plus their tasks, their lists and
 * any inputs running low.
 */
const WarehouseDashboard: React.FC = () => {
  const { user } = useAuth();
  const { ready: tasksReady, lists, itemsForList, members, me } = useTasks();
  const navigate = useNavigate();
  const [data, setData] = useState<WarehouseDashboardData | null>(null);
  const [lowInputs, setLowInputs] = useState<LowInput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/dashboard', { params: { view: 'warehouse' } });
      setData(response.data);
    } catch (err: any) {
      console.error('Warehouse dashboard fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  // A failure here must not break the page — the inputs card just stays hidden.
  const fetchInputs = useCallback(async () => {
    try {
      const response = await api.get('/inputs');
      setLowInputs((response.data.inputs || []).filter((i: LowInput) => ['out', 'critical', 'low'].includes(i.health)));
    } catch (err) {
      console.error('Warehouse dashboard inputs fetch error:', err);
      setLowInputs([]);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchInputs();
  }, [fetchData, fetchInputs]);

  const ordersSort = useTableSort<WarehouseOrderRow>(data?.orders || [], 'age_days', 'desc');

  // Lists I own or am on (the MPP week list isn't one of them), busiest first.
  const myLists = useMemo(() => {
    if (!tasksReady) return [];
    return lists
      .filter((l) => l.kind !== 'weekly' && (l.owner_email === me || (members.get(l.id) || []).includes(me)))
      .map((l) => {
        const top = itemsForList(l.id).filter((i) => i.depth === 0);
        return { list: l, open: top.filter((i) => i.progress < 100).length, total: top.length };
      })
      .sort((a, b) => Number(b.open > 0) - Number(a.open > 0) || b.list.created_at.localeCompare(a.list.created_at));
  }, [tasksReady, lists, itemsForList, members, me]);
  const openRequests = useMemo(
    () =>
      myLists.reduce(
        (n, { list }) =>
          n + itemsForList(list.id).filter((i) => i.source === 'request' && !i.linked_to && i.depth === 0 && i.progress < 100).length,
        0
      ),
    [myLists, itemsForList]
  );

  if (loading) return <LoadingSpinner />;
  if (error) {
    return (
      <div className="dash-container">
        <div className="dash-error">
          Error: {error}{' '}
          <button className="dash-retry-button" onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const first = (user?.name || '').split(' ')[0];
  const multiWarehouse = data.warehouses.length > 1;
  const openOrder = (o: WarehouseOrderRow) => navigate(`/warehouse-${o.warehouse_id}-orders?order=${o.order_id}`);

  const kpiCards: { label: string; value: string; sub: string; alert?: boolean; onClick?: () => void }[] = [
    { label: 'To Ship', value: String(data.kpis.toShip), sub: 'orders waiting on your warehouse' },
    {
      label: 'Old Orders',
      value: String(data.kpis.old),
      sub: `waiting ${data.old_order_days}+ days`,
      alert: data.kpis.old > 0,
    },
    { label: 'With Notes', value: String(data.kpis.withNotes), sub: 'read before shipping', alert: data.kpis.withNotes > 0 },
    { label: 'Open Requests', value: String(openRequests), sub: 'waiting on MPP', onClick: () => navigate('/inputs') },
  ];

  return (
    <div className="dash-container">
      <div className="dash-header">
        <h1>{first ? `Hi ${first}` : 'Dashboard'}</h1>
        <span className="dash-updated">
          {data.warehouses.map((w) => w.name).join(' / ')} · Updated {new Date(data.generated_at).toLocaleTimeString('en-US')}
        </span>
      </div>

      <div className="dash-kpi-grid">
        {kpiCards.map((kpi) => (
          <div
            className={`dash-kpi-card ${kpi.alert ? 'alert' : ''} ${kpi.onClick ? 'clickable' : ''}`}
            key={kpi.label}
            onClick={kpi.onClick}
            role={kpi.onClick ? 'link' : undefined}
          >
            <span className="dash-kpi-label">{kpi.label}</span>
            <span className="dash-kpi-value">{kpi.value}</span>
            <span className="dash-kpi-sub">{kpi.sub}</span>
          </div>
        ))}
      </div>

      <div className={`dash-card ${data.kpis.old > 0 ? 'dash-card-critical' : ''}`}>
        <div className="dash-card-header">
          <h3>Orders To Ship</h3>
          {data.warehouses.length === 1 && (
            <Link to={`/warehouse-${data.warehouses[0].id}-orders`} className="dash-link">Go to Orders →</Link>
          )}
        </div>
        <p className="dash-card-note">
          Oldest first. Orders waiting {data.old_order_days} days or more are red; shipment notes are shown under the order.
          Click an order to open it.
        </p>
        {data.orders.length === 0 ? (
          <p className="dash-empty">Nothing waiting — every order has shipped. 🎉</p>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <SortableTh label="Order" column="order_id" sort={ordersSort} />
                  {multiWarehouse && <SortableTh label="Warehouse" column="warehouse" sort={ordersSort} />}
                  <SortableTh label="Customer" column="customer_name" sort={ordersSort} />
                  <SortableTh label="Waiting To Ship" column="pending_items" sort={ordersSort} />
                  <SortableTh label="Ordered" column="date_created" sort={ordersSort} />
                  <SortableTh label="Age" column="age_days" center sort={ordersSort} />
                </tr>
              </thead>
              <tbody>
                {ordersSort.sorted.map((o) => {
                  const hasNotes = o.notes.length > 0;
                  return (
                    <React.Fragment key={o.shipment_id}>
                      <tr
                        className={`dash-row-clickable${hasNotes ? ' dash-row-has-note' : ''}`}
                        title="Open this order"
                        onClick={() => openOrder(o)}
                      >
                        <td>
                          #{o.order_id}
                          {hasNotes && <span className="dash-chip warn dash-note-chip">Note</span>}
                        </td>
                        {multiWarehouse && <td className="dash-warehouse">{o.warehouse}</td>}
                        <td>{o.customer_name}</td>
                        <td>
                          {o.pending_items} of {o.total_items}
                          {o.pending_names && <span className="dash-sku"> · {o.pending_names}</span>}
                        </td>
                        <td>{formatDate(o.date_created)}</td>
                        <td style={{ textAlign: 'center' }}>{ageChip(o.age_days, data.old_order_days)}</td>
                      </tr>
                      {hasNotes && (
                        <tr className="dash-note-row" onClick={() => openOrder(o)}>
                          <td colSpan={multiWarehouse ? 6 : 5}>
                            {o.notes.map((n, i) => (
                              <div className="dash-note" key={i}>
                                <strong>{n.added_by}:</strong> {n.note}
                              </div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <MyTasksCard />

      <div className="dash-grid-2 dash-warehouse-grid">
        <div className="dash-card">
          <div className="dash-card-header">
            <h3>Your Lists</h3>
            <Link to="/lists" className="dash-link">Go to Lists →</Link>
          </div>
          {!tasksReady ? (
            <p className="dash-empty">Loading…</p>
          ) : myLists.length === 0 ? (
            <p className="dash-empty">You're not on any lists yet. Requests you send from Inputs show up here.</p>
          ) : (
            <ul className="dash-list-links">
              {myLists.slice(0, LISTS_SHOWN).map(({ list, open, total }) => (
                <li key={list.id}>
                  <button type="button" onClick={() => navigate(`/lists?list=${list.id}`)}>
                    <span className="dash-list-title">{list.title || 'Untitled list'}</span>
                    <span className={`dash-chip ${open > 0 ? 'neutral' : 'ok'}`}>
                      {total === 0 ? 'Empty' : open > 0 ? `${open} open` : 'All done'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {myLists.length > LISTS_SHOWN && (
            <p className="dash-card-note mt-foot">
              <Link to="/lists" className="dash-link">+{myLists.length - LISTS_SHOWN} more</Link>
            </p>
          )}
        </div>

        <div className="dash-card">
          <div className="dash-card-header">
            <h3>Inputs Running Low</h3>
            <Link to="/inputs" className="dash-link">Go to Inputs →</Link>
          </div>
          {lowInputs.length === 0 ? (
            <p className="dash-empty">Your warehouse's inputs are all stocked.</p>
          ) : (
            <ul className="dash-list-links">
              {lowInputs.map((i) => (
                <li key={i.id}>
                  <button type="button" onClick={() => navigate(`/inputs?input=${i.id}`)}>
                    <span className="dash-list-title">{i.name}</span>
                    <span className={`dash-chip ${i.health === 'low' ? 'warn' : 'danger'}`}>
                      {i.health === 'out' ? 'Out' : i.days_cover !== null ? `${i.days_cover} days left` : 'Low'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

/** Non-admins: a personal landing page with their open tasks. */
const PersonalDashboard: React.FC = () => {
  const { user } = useAuth();
  const first = (user?.name || '').split(' ')[0];
  return (
    <div className="dash-container">
      <div className="dash-header">
        <h1>{first ? `Hi ${first}` : 'Dashboard'}</h1>
      </div>
      <MyTasksCard />
    </div>
  );
};

const Dashboard: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const { permissions } = useAuth();
  if (isAdmin) return <AdminDashboard />;
  if ((permissions?.editable_warehouses || []).length) return <WarehouseDashboard />;
  return <PersonalDashboard />;
};

export default Dashboard;
